/**
 * Leituras de resumo de um pedido, cada uma escrita numa célula só.
 *
 * Papel: aqui ficam o fornecedor, a descrição, a casa e o resultado que
 * representam um pedido inteiro numa linha — o que o cartão do quadro, a
 * tabela de finalizados e a planilha deles mostram no lugar de cada item, da
 * residência aninhada e do motivo de reprovação.
 *
 * Como usar: nenhuma delas é campo do pedido, e nenhuma deverá virar um. Um
 * pedido pode ter itens de fornecedores diferentes, e guardar o resumo ao lado
 * dos itens criaria a cópia que envelhece no primeiro item corrigido. Quem
 * precisar de outra leitura de resumo a acrescenta aqui, para o quadro, a
 * tabela e a planilha continuarem dizendo a mesma coisa do mesmo pedido.
 */
import { type PedidoCompra } from '@concierge/shared';

import { ROTULO_SEM_RESIDENCIA } from './rotulos';

/**
 * O fornecedor que representa o pedido.
 *
 * É o do primeiro item que tenha um nome não vazio — os demais itens podem ser
 * de outros fornecedores, e é por isso que o resumo nunca é gravado.
 *
 * @param pedido - O pedido, com os itens aninhados — o gravado, ou qualquer
 *   outro com `itens`, como o da folha de aprovação.
 * @returns O nome, sem espaços nas pontas, ou `null` quando nenhum item tem.
 */
export function fornecedorDoPedido(pedido: {
  itens: readonly { fornecedor?: string | null }[];
}): string | null {
  for (const item of pedido.itens) {
    const nome = item.fornecedor?.trim() ?? '';
    if (nome !== '') return nome;
  }
  return null;
}

/**
 * A descrição que representa o pedido numa única célula.
 *
 * Junta as descrições dos itens por vírgula; um pedido sem item cai na
 * observação e, sem ela, no próprio número — a célula nunca sai vazia, porque
 * uma linha sem descrição na tabela de finalizados não diz de que compra se
 * trata.
 *
 * @param pedido - O pedido, com os itens aninhados.
 * @returns O texto da descrição, nunca vazio.
 */
export function descricaoDoPedido(pedido: PedidoCompra): string {
  const dosItens = pedido.itens
    .map((item) => item.descricao)
    .filter((descricao) => descricao !== '')
    .join(', ');
  if (dosItens !== '') return dosItens;
  if (pedido.observacao !== null && pedido.observacao !== '') return pedido.observacao;
  return `Pedido de compra ${pedido.numero}`;
}

/**
 * A casa do pedido, como o cartão e a tabela a escrevem.
 *
 * O pedido com `residencia` nula mostra `ROTULO_SEM_RESIDENCIA`, e nunca uma
 * célula vazia: a ausência de casa é uma escolha feita no formulário, e a linha
 * precisa dizer qual foi.
 *
 * @param pedido - O pedido, com a residência aninhada ou nula.
 * @returns O nome da casa, ou o texto do pedido que não é de casa nenhuma.
 */
export function residenciaDoPedido(pedido: PedidoCompra): string {
  return pedido.residencia?.nome ?? ROTULO_SEM_RESIDENCIA;
}

/**
 * Como o pedido finalizado terminou, na palavra da pílula de Resultado.
 *
 * O pedido com `motivoReprovacao` preenchido foi reprovado; sem motivo, foi
 * entregue e confirmado. A tabela, a busca, a ordenação e a planilha leem a
 * mesma palavra, e é por ela que o reprovado se encontra digitando
 * "reprovado".
 *
 * @param pedido - Um pedido `finalizado`.
 * @returns `Concluído` ou `Reprovado`.
 */
export function resultadoDoPedido(pedido: PedidoCompra): 'Concluído' | 'Reprovado' {
  return pedido.motivoReprovacao === null ? 'Concluído' : 'Reprovado';
}
